import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Chip, Box } from "@mui/material";
import { BaseTabs } from "components/Shared/BaseTabs";
import TableFilter from "components/Shared/AppTable/TableFilter";
import BaseSelect from "components/Shared/Base/BaseSelect";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { useLazyListCollectionForContractorQuery } from "store/services/collectionApi";
import { useAppMeta, useTablePagination } from "hooks";
import { useAuth } from "hooks/useAuth";

const getStatusStyle = (status) => {
  const styles = {
    COMPLETED: { backgroundColor: "#EBF5FB", textColor: "green" },
    CANCELLED: { backgroundColor: "#FDEDEC", textColor: "#E74C3C" },
  };
  return styles[status?.toUpperCase()] || {
    backgroundColor: "#F8F9F9",
    textColor: "#7F8C8D",
  };
};

const ContractorCollectionListScreen = () => {
  const { loggedInUser } = useAuth();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";
  const [sortOrder, setSortOrder] = useState("createdAt:-1");
  
  const breadcrumbs = [{ title: "Collections" }];
  const fulfillmentListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const [fetchFulfillments, { data: fulfillmentRes, isLoading, isFetching, error }] = useLazyListCollectionForContractorQuery();

  useEffect(() => {
    if (loggedInUser && loggedInUser.typeRef && loggedInUser.typeRefSource === "Contractor") {
      const [sortedBy, sortDirection] = sortOrder.split(":");
      fetchFulfillments({
        active: activeTab === "Active",
        page: currentPage,
        limit: perPage,
        ...(keyword && { keyword }),
        sortedBy,
        sortDirection: parseInt(sortDirection),
        contractorUser: loggedInUser.typeRef,

      });
      
    }
  }, [activeTab, currentPage, fetchFulfillments, keyword, perPage, sortOrder, loggedInUser]);

  useAppMeta(`${activeTab} Collections`);

  const columns = [
    {
      name: "Collection ID",
      selector: (row) => row.collectionId,
      cell: (row) => (
        <Link
          title={row?.collectionId}
          to={`/app/contractor/myShipments/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.collectionId ? (
            <HighlightedText text={row?.collectionId} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Shipment ID(s)",
      selector: row => row.shipmentId.map(s => s.shipmentId).join(", "),
      cell: row =>
        row.shipmentId.length
          ? row.shipmentId.map((s, i) => (
              <React.Fragment key={s._id || i}>
                <Link
                  title={s.shipmentId}
                  to={`/app/contractor/shipments/${s._id}/detail`}
                  className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                >
                  <HighlightedText text={s.shipmentId} highlight={keyword} />
                </Link>
                {i < row.shipmentId.length - 1 && ", "}
              </React.Fragment>
            ))
          : "N/A",
      sortable: false,
    },
    // Contractor
    {
      name: "Contractor",
      selector: row => row.contractorId?.name || "N/A",
      cell: row =>
        row.contractorId
          ? `${row.contractorId.name}`
          : "N/A",
      sortable: true,
    },
  
    // Vehicle
    {
      name: "Vehicle",
      selector: row => row.vehicle_id?.vehicle_id || "N/A",
      cell: row =>
        row.vehicle_id
          ? `${row.vehicle_id.vehicle_id}`
          : "N/A",
      sortable: true,
    },
  
    // Driver
    {
      name: "Driver",
      selector: row => row.driverId?.name || "N/A",
      cell: row =>
        row.driverId
          ? `${row.driverId.name}`
          : "N/A",
      sortable: true,
    },
    
    {
      name: "Pickup Time",
      selector: (row) => <AppTableDateTime date={row?.actualPickupTime} />,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Chip
            label={row?.status?.toUpperCase() || "N/A"}
            sx={{
              backgroundColor: getStatusStyle(row?.status).backgroundColor,
              color: getStatusStyle(row?.status).textColor,
              fontWeight: "bold",
              height: "17px",
              borderRadius: "12px",
              width: "auto",
              padding: "0",
              border: "none",
              "& .MuiChip-label": {
                fontSize: "0.6rem",
                padding: "3px 5px",
                lineHeight: "14px",
              },
            }}
          />
        </Box>
      ),
      center: true,
    },
  ];

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <BaseTabs tabs={fulfillmentListTabs} defaultValue={activeTab} />
        <TableFilter searchLabel="Search Fulfillments" disabled={isLoading || isFetching} initialFilters={2}>
          <BaseSelect
            fullWidth
            label="Sort By"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            options={[
              { label: "CreatedAt (Desc)", value: "createdAt:-1" },
              { label: "CreatedAt (Asc)", value: "createdAt:1" },
              { label: "UpdatedAt (Desc)", value: "updatedAt:-1" },
              { label: "UpdatedAt (Asc)", value: "updatedAt:1" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />
        </TableFilter>
        <AppTable
          columns={columns}
          data={fulfillmentRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={fulfillmentRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default ContractorCollectionListScreen;
